import React, { useContext } from 'react'
import { ReactElement } from 'react'
import { useState } from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CircularProgress from '@mui/material/CircularProgress'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { SectionUserContext } from 'common/contexts/SectionUserContext'
import useBannerNotification from 'modules/notifications/banners/useBannerNotification'
import useProjectPlans from 'modules/projects/hooks/usePlans'
import useProjects from 'modules/projects/hooks/useProjects'

function Support() {
  const { push } = useBannerNotification()
  const { sectionUser: user, selectedAccount: accountId } = useContext(SectionUserContext)
  const { projects } = useProjects(accountId.toString() as string)
  const { projectPlans } = useProjectPlans()
  const firstName = user.first_name ? user.first_name : ''
  const lastName = user.last_name ? user.last_name : ''
  const separator = firstName === '' || lastName === '' ? '' : ' '
  const fullName = firstName + separator + lastName

  const [requestType, setRequestType] = useState('')
  const [subject, setSubject] = useState('')
  const [project, setProject] = useState('')
  const [description, setDescription] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  let projectsDropdown: ReactElement | string = ''

  const projectArr = project.split('|')

  const projectObj = {
    name: '',
    id: '',
    plan: '',
    environment: {
      id: '',
    },
    isKei: '',
  }

  if (projects !== null && projects.length > 0 && (requestType === 'technical' || requestType === 'billing')) {
    projectsDropdown = (
      <Box sx={{ marginBottom: '16px' }}>
        <FormControl fullWidth>
          <TextField
            select
            id="project"
            label={'Project'}
            value={project}
            onChange={(event) => setProject(event.target.value)}
          >
            {projects.map((option) => (
              <MenuItem
                key={option.name}
                value={
                  option.name +
                  '|' +
                  option.id +
                  '|' +
                  option.planId +
                  '|' +
                  option.environments[0].id +
                  '|' +
                  option.is_kei
                }
              >
                {option.name} ({option.id})
              </MenuItem>
            ))}
          </TextField>
        </FormControl>
      </Box>
    )

    if (projectArr.length >= 5) {
      projectObj.name = projectArr[0] ? projectArr[0] : projectObj.name
      projectObj.id = projectArr[1] ? projectArr[1] : projectObj.id

      if (projectArr[2]) {
        const planDisplayName = projectPlans
          ?.filter((plan) => plan.id === projectArr[2])[0]
          .displayName?.split(' - ')[0]
        projectObj.plan = planDisplayName ? planDisplayName : projectObj.plan
      }

      projectObj.environment.id = projectArr[3] ? projectArr[3] : projectObj.environment.id
      projectObj.isKei = projectArr[4] ? projectArr[4] : projectObj.isKei
    }
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    try {
      setIsSubmitting(true)
      const response = await fetch('https://support-api.section.io/request/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          requester: {
            name: fullName ? fullName : user.email,
            email: user.email,
            account: {
              id: accountId.toString(),
            },
          },
          requestType: requestType,
          subject: subject,
          project: {
            name: projectObj.name,
            id: projectObj.id,
            plan: projectObj.plan,
            environment: {
              id: projectObj.environment.id,
            },
            isKei: projectObj.isKei,
          },
          comment: {
            body: description,
          },
          priority: requestType === 'feedback' ? 'low' : 'normal',
        }),
      })

      const responseObj = await response.json()

      if (responseObj.status === 201) {
        let responseTime = 'shortly'

        if (projectObj.plan === 'Standard') {
          responseTime = 'within 48 hours'
        }

        if (projectObj.plan === 'Pro') {
          responseTime = 'within 24 hours'
        }

        push({
          title: 'Success',
          variant: 'success',
          message:
            'Your support request has been received (#' +
            responseObj.data.request.id +
            '). Our team is reviewing it and will reply ' +
            responseTime +
            '.',
        })

        setRequestType('')
        setSubject('')
        setProject('')
        setDescription('')
        setIsSubmitting(false)
      } else {
        setIsSubmitting(false)

        // Any response other than 201 is being returned by Zendesk.
        // TODO: Replace support@section.io with contact form link or generic email address once created (e.g. hello/contact/@section.io).
        push({
          title: 'Error',
          variant: 'error',
          message:
            'Your support request was unable to be submitted (code 1). Please try again or email support@section.io if this issue persists.',
        })
      }
    } catch (error) {
      setIsSubmitting(false)

      // https://support-api.section.io is not responding.
      // TODO: Replace support@section.io with contact form link or generic email address once created (e.g. hello/contact/@section.io).
      push({
        title: 'Error',
        variant: 'error',
        message:
          'Your support request was unable to be submitted (code 2). Please try again or email support@section.io if this issue persists.',
      })
    }
  }

  return (
    <>
      <Box p={5} flex={0}>
        <Grid
          container
          alignItems="center"
          direction="row"
          spacing={2}
          sx={{
            color: 'primaryColors.offWhite',
          }}
        >
          <Grid item>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <Typography
                variant="h5"
                sx={{ color: 'primaryColors.modalBlack', marginTop: '2px', marginBottom: '2px' }}
              >
                Support
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box p={5} sx={{ display: 'block', height: '100%' }}>
        <Card
          sx={{
            minWidth: 475,
            maxWidth: 975,
            height: '100%',
            backgroundColor: 'primaryColors.white',
            color: 'primaryColors.modalBlack',
          }}
        >
          <CardContent
            sx={{
              padding: 0,
              '&:last-child': {
                paddingBottom: 0,
              },
            }}
          >
            <Box p={5} sx={{ display: 'block', height: '100%' }}>
              <form onSubmit={handleSubmit}>
                <Box sx={{ marginBottom: '32px' }}>
                  <p>
                    Have a question or need help? Open a support request below and someone from our team will be happy
                    to assist.
                  </p>
                </Box>
                <Box>
                  <FormControl fullWidth sx={{ flexDirection: 'row' }}>
                    <TextField
                      required
                      select
                      id="request-type"
                      label={'Request Type'}
                      value={requestType}
                      onChange={(event) => setRequestType(event.target.value)}
                      sx={{ width: '20ch', marginRight: '16px', marginBottom: '16px' }}
                    >
                      <MenuItem key="technical" value="technical">
                        Technical
                      </MenuItem>
                      <MenuItem key="account" value="account">
                        Account
                      </MenuItem>
                      <MenuItem key="billing" value="billing">
                        Billing
                      </MenuItem>
                      <MenuItem key="feedback" value="feedback">
                        Feedback
                      </MenuItem>
                    </TextField>
                    <TextField
                      required
                      fullWidth
                      id="subject"
                      label={'Subject'}
                      value={subject}
                      onChange={(event) => setSubject(event.target.value)}
                      sx={{ marginBottom: '16px' }}
                    />
                  </FormControl>
                </Box>
                {projectsDropdown}
                <Box sx={{ marginBottom: '16px' }}>
                  <FormControl fullWidth>
                    <TextField
                      required
                      id="description"
                      label={'Description'}
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                      multiline
                      minRows={8}
                    />
                  </FormControl>
                </Box>
                <Box sx={{ alignItems: 'center', display: 'flex' }}>
                  <Button
                    color="primary"
                    variant="contained"
                    sx={{
                      marginLeft: 'auto',
                      backgroundColor: 'primaryColors.regGreen',
                      color: 'white',
                    }}
                    type="submit"
                    disabled={isSubmitting}
                  >
                    Submit
                    {isSubmitting && (
                      <CircularProgress
                        size={24}
                        sx={{
                          color: 'primaryColors.alertRed',
                          position: 'absolute',
                        }}
                      />
                    )}
                  </Button>
                </Box>
              </form>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </>
  )
}

export default Support
